import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import * as FaIcons from "react-icons/fa";
import { FaBars, FaChevronDown, FaChevronRight } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";

import {
    getMenu,
    refreshMenuCache,
    refreshMenuCacheOnly,
} from "../api/menuApi";
import "@fortawesome/fontawesome-free/css/all.min.css";
import socket from "../socket";

const getFaIcon = (iconName) => {
    if(!iconName) return null;
    const Icon = FaIcons[iconName];
    return typeof Icon === "function" ? Icon : null;
};

const resolveMenuPath = (path) =>
    path === "News-management" ? "BlogPost-management" : path;

const MenuItem = ({
    item,
    openSections,
    toggleSection,
    currentPage,
    isParent = false,
    isSidebarOpen,
    setIsSidebarOpen,
    setOpenSections,
    setOpenSection,
}) => {
    MenuItem.propTypes = {
        item: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            seo_page: PropTypes.string,
            title: PropTypes.string,
            is_enabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
            icon: PropTypes.string,
            children: PropTypes.arrayOf(PropTypes.object),
        }).isRequired,
        openSections: PropTypes.object.isRequired,
        toggleSection: PropTypes.func.isRequired,
        currentPage: PropTypes.string,
        isParent: PropTypes.bool,
        isSidebarOpen: PropTypes.bool.isRequired,
        setIsSidebarOpen: PropTypes.func.isRequired,
        setOpenSections: PropTypes.func.isRequired,
        setOpenSection: PropTypes.func.isRequired,
    };
    const navigate = useNavigate();

    // If sidebar is closed, render just the icon
    const urlPath =
        item.seo_page && item.seo_page.trim() !== ""
            ? resolveMenuPath(item.seo_page)
            : item.title;
    if(!isSidebarOpen) {
        return (
            <div
                className={`flex justify-center cursor-pointer p-1.5 sm:p-2 hover:bg-[#C2D7E2] rounded-md mb-1 w-8 h-8 sm:w-10 sm:h-10 ${
                    currentPage === urlPath ? "bg-[#C2D7E2]" : ""
                }`}
                onClick={() => {
                    navigate(`/${urlPath}`);
                    // Close sidebar on mobile after navigation
                    const isMobile = window.innerWidth < 1024;
                    if(isMobile) {
                        setIsSidebarOpen(false);
                        setOpenSections({});
                        setOpenSection(null);
                    }
                }}
                title={item.title}
            >
                {(() => {
                    const Icon = getFaIcon(item.icon);
                    return Icon ? (
                        <Icon
                            size={window.innerWidth < 640 ? 12 : 16}
                            color="#D4AF37"
                        />
                    ) : null;
                })()}
            </div>
        );
    }

    return (
        <div key={item.id} className="ml-1 sm:ml-2 lg:ml-4">
            <div
                className={`flex items-center justify-between p-1.5 sm:p-2 rounded-md cursor-pointer w-full text-[11px] sm:text-[13px]
          ${
              currentPage === urlPath
                  ? "bg-[#C2D7E2] text-navy"
                  : "text-white hover:bg-[#C2D7E2] hover:text-navy"
          }`}
                onClick={() => {
                    if(item.children.length > 0) {
                        toggleSection(item.id, isParent);
                    } else {
                        navigate(`/${urlPath}`);
                        // Close sidebar on mobile after navigation
                        const isMobile = window.innerWidth < 1024;
                        if(isMobile) {
                            setIsSidebarOpen(false);
                            setOpenSections({});
                            setOpenSection(null);
                        }
                    }
                }}
            >
                <div className="flex items-center space-x-1 sm:space-x-2 w-full min-w-0">
                    {(() => {
                        const Icon = getFaIcon(item.icon);
                        return Icon ? (
                            <Icon
                                color="#D4AF37"
                                size={window.innerWidth < 640 ? 12 : 16}
                            />
                        ) : null;
                    })()}
                    <span className="truncate">{item.title}</span>
                </div>

                {item.children.length > 0 &&
                    (openSections[item.id] ? (
                        <FaChevronDown
                            size={window.innerWidth < 640 ? 10 : 12}
                            className="flex-shrink-0"
                        />
                    ) : (
                        <FaChevronRight
                            size={window.innerWidth < 640 ? 10 : 12}
                            className="flex-shrink-0"
                        />
                    ))}
            </div>

            {openSections[item.id] && (
                <div className="ml-1 sm:ml-2 lg:ml-4 mt-1 space-y-1 sm:space-y-2">
                    {item.children.map((subItem) => (
                        <MenuItem
                            key={subItem.id}
                            item={subItem}
                            openSections={openSections}
                            currentPage={currentPage}
                            toggleSection={toggleSection}
                            isSidebarOpen={isSidebarOpen}
                            setIsSidebarOpen={setIsSidebarOpen}
                            setOpenSections={setOpenSections}
                            setOpenSection={setOpenSection}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const Sidebar = ({ userId, user, isSidebarOpen, setIsSidebarOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [menu, setMenu] = useState({ general: [], core_admin: [] });
    const [openSections, setOpenSections] = useState({});
    const [openSection, setOpenSection] = useState(null);
    const userRole = user?.role;
    const currentPage = decodeURIComponent(
        location.pathname.split("/")[1] || "",
    );

    // Expose fetchMenu for debugging purposes
    useEffect(() => {
        if(
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1"
        ) {
            window.debugRefreshMenu = () => {
                fetchMenu(true);
            };

            return () => {
                delete window.debugRefreshMenu;
            };
        }
    }, []);

    // Periodic refresh as fallback (every 10 minutes)
    useEffect(() => {
        const effectiveUserId = userId || user?.id;
        if(!effectiveUserId) return;

        const periodicRefresh = setInterval(
            () => {
                fetchMenu(true);
            },
            10 * 60 * 1000,
        );

        return () => clearInterval(periodicRefresh);
    }, [userId, user?.id]);

    // Define fetchMenu function
    const fetchMenu = async (
        forceRefresh = false,
        useMenuCacheOnly = false,
    ) => {
        // Try to get userId from prop or user object
        let effectiveUserId = userId;
        if(!effectiveUserId && user?.id) {
            effectiveUserId = user.id;
        }

        if(
            !effectiveUserId ||
            effectiveUserId === "undefined" ||
            effectiveUserId === "null"
        ) {
            return;
        }

        // Ensure userId is valid
        const numericUserId = parseInt(effectiveUserId, 10);
        if(isNaN(numericUserId) || numericUserId <= 0) {
            return;
        }

        try {
            if(useMenuCacheOnly) {
                // Use the menu-specific cache refresh for security group changes
                await refreshMenuCacheOnly(numericUserId);
            } else {
                await refreshMenuCache(numericUserId);
            }

            // Add small delay to ensure cache is cleared before fetching
            if(forceRefresh) {
                await new Promise((resolve) => setTimeout(resolve, 100));
            }

            const menuData = await getMenu(numericUserId, true);

            // Only update if we got valid data
            if(menuData && typeof menuData === "object") {
                setMenu(menuData);
            }
        } catch(error) {
            // Retry once after a short delay if initial attempt fails
            if(!forceRefresh) {
                setTimeout(() => fetchMenu(true), 1000);
            }
        }
    };

    // Load sidebar state from localStorage on mount
    useEffect(() => {
        const savedSidebarState = localStorage.getItem("isSidebarOpen");
        const isMobile = window.innerWidth < 1024;

        if(isMobile) {
            // Always start closed on mobile
            setIsSidebarOpen(false);
        } else if(savedSidebarState !== null) {
            // Use saved state for desktop
            setIsSidebarOpen(JSON.parse(savedSidebarState));
        } else {
            // Default to open on desktop
            setIsSidebarOpen(true);
        }

        // Handle window resize
        const handleResize = () => {
            const isMobileNow = window.innerWidth < 1024;
            if(isMobileNow) {
                // Always close on mobile
                setIsSidebarOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [setIsSidebarOpen]);

    useEffect(() => {
        const effectiveUserId = userId || user?.id;
        if(!effectiveUserId) {
            return;
        }

        fetchMenu();

        // Setup socket listeners with improved handling
        const handleMenuUpdate = () => {
            fetchMenu(true);
        };

        const handleNotification = () => {
            fetchMenu(true);
        };

        const handleSecurityGroupUpdate = () => {
            // Use menu-specific cache refresh
            fetchMenu(true, true);
        };

        socket.on("menu-update", handleMenuUpdate);
        socket.on("notification", handleNotification);
        socket.on("security-group-updated", handleSecurityGroupUpdate);

        // Cleanup listeners on component unmount or userId change
        return () => {
            socket.off("menu-update", handleMenuUpdate);
            socket.off("notification", handleNotification);
            socket.off("security-group-updated", handleSecurityGroupUpdate);
        };
    }, [userId, user?.id]);

    // Auto-expand sidebar sections based on current page
    useEffect(() => {
        if(!menu || (!menu.general?.length && !menu.core_admin?.length)) {
            return;
        }

        const findActiveMenuItem = (menuItems, path) => {
            for(const item of menuItems) {
                const itemPath =
                    item.seo_page && item.seo_page.trim() !== ""
                        ? item.seo_page
                        : item.title;

                // Check if this item matches the current path
                if(itemPath === path) {
                    return { item, parentSection: null };
                }

                // Check children
                if(item.children && item.children.length > 0) {
                    for(const child of item.children) {
                        const childPath =
                            child.seo_page && child.seo_page.trim() !== ""
                                ? child.seo_page
                                : child.title;
                        if(childPath === path) {
                            return {
                                item: child,
                                parentSection: item,
                                parentItem: null,
                            };
                        }

                        // Check grandchildren
                        if(child.children && child.children.length > 0) {
                            for(const grandchild of child.children) {
                                const grandchildPath =
                                    grandchild.seo_page &&
                                    grandchild.seo_page.trim() !== ""
                                        ? grandchild.seo_page
                                        : grandchild.title;
                                if(grandchildPath === path) {
                                    return {
                                        item: grandchild,
                                        parentSection: item,
                                        parentItem: child,
                                    };
                                }
                            }
                        }
                    }
                }
            }
            return null;
        };

        // Find the active menu item in both general and admin menus
        const allSections = [
            ...(menu.general || []),
            ...(menu.core_admin || []),
        ];
        const activeMenuItem = findActiveMenuItem(allSections, currentPage);

        if(activeMenuItem) {
            const { parentSection, parentItem } = activeMenuItem;

            // Auto-expand the necessary sections
            if(parentSection) {
                // Set the main section as open
                setOpenSection(parentSection.id);

                // If there's a parent item (nested structure), expand it too
                if(parentItem) {
                    setOpenSections((prev) => ({
                        ...prev,
                        [parentItem.id]: true,
                    }));
                }
            }
        }
    }, [menu, currentPage]);

    useEffect(() => {
        const handleMenuUpdate = () => {
            fetchMenu(true);
        };

        const handleSecurityGroupUpdate = () => {
            fetchMenu(true, true);
        };

        // Listen for the custom events
        window.addEventListener("menu-updated", handleMenuUpdate);
        window.addEventListener(
            "security-group-updated",
            handleSecurityGroupUpdate,
        );
        window.addEventListener("user-permissions-updated", handleMenuUpdate);

        // Cleanup the event listeners on component unmount
        return () => {
            window.removeEventListener("menu-updated", handleMenuUpdate);
            window.removeEventListener(
                "security-group-updated",
                handleSecurityGroupUpdate,
            );
            window.removeEventListener(
                "user-permissions-updated",
                handleMenuUpdate,
            );
        };
    }, []);

    const toggleSidebar = () => {
        setIsSidebarOpen((prev) => {
            const newState = !prev;
            const isMobile = window.innerWidth < 1024;

            // Only save state to localStorage if not mobile
            if(!isMobile) {
                localStorage.setItem("isSidebarOpen", JSON.stringify(newState));
            }

            return newState;
        });
        if(isSidebarOpen) {
            setOpenSections({});
            setOpenSection(null);
        }
    };
    // const handleLogout = () => {
    //   localStorage.clear()
    //   navigate("/login")
    // }

    const toggleSection = (id, isParent) => {
        setOpenSections((prev) => {
            const isCurrentlyOpen = prev[id];
            return isParent
                ? isCurrentlyOpen
                    ? { ...prev, [id]: false }
                    : { [id]: true }
                : { ...prev, [id]: !isCurrentlyOpen };
        });
    };

    const handleSectionClick = (sectionId, sectionTitle, hasChildren) => {
        if(isSidebarOpen) {
            if(hasChildren) {
                // Toggle the section if it has children
                setOpenSection((prev) =>
                    prev === sectionId ? null : sectionId,);
            } else {
                // Find the section object to get seo_page if available
                const allSections = [
                    ...(menu.general || []),
                    ...(menu.core_admin || []),
                ];
                const sectionObj = allSections.find(
                    (sec) => sec.id === sectionId,
                );
                const urlPath =
                    sectionObj &&
                    sectionObj.seo_page &&
                    sectionObj.seo_page.trim() !== ""
                        ? resolveMenuPath(sectionObj.seo_page)
                        : sectionTitle.toLowerCase();
                navigate(`/${urlPath}`);

                // Close sidebar on mobile after navigation
                const isMobile = window.innerWidth < 1024;
                if(isMobile) {
                    setIsSidebarOpen(false);
                    setOpenSections({});
                    setOpenSection(null);
                }
            }
        }
    };

    // Function to flatten the menu structure to get all menu items
    // Insert this right here:
    const getAllMenuItems = () => {
        const allItems = [];

        if(!menu || typeof menu !== "object") {
            // console.warn(" Menu is not a valid object:", menu);
            return allItems;
        }

        const sections = [...(menu.general || []), ...(menu.core_admin || [])];

        sections.forEach((section) => {
            allItems.push(section);

            if(Array.isArray(section.children)) {
                section.children.forEach((item) => {
                    allItems.push(item);

                    if(Array.isArray(item.children)) {
                        item.children.forEach((subItem) => {
                            allItems.push(subItem);
                        });
                    }
                });
            }
        });
        // allItems.forEach((item, index) => {
        //   console.log(`[${index}] ${item.title || "Untitled"} →`, {
        //     id: item.id,
        //     icon: item.icon,
        //     item_type: item.item_type,
        //     path: item.path,
        //   });
        // });

        return allItems;
    };

    return (
        <div
            className={`h-screen relative ${
                isSidebarOpen ? "w-48 sm:w-56 lg:w-64" : "w-12 sm:w-14 lg:w-16"
            } text-white overflow-y-auto transition-all duration-300 flex flex-col`}
            style={{ backgroundColor: "var(--navy)" }}
        >
            {/* Top Bar */}
            <div className="flex items-center justify-between p-2 sm:p-3 lg:p-4 flex-shrink-0">
                {/* Logo + Brand (Left) */}
                <div
                    className="flex items-center space-x-1 sm:space-x-2 cursor-pointer"
                    onClick={() => {
                        navigate("/");
                        // Close sidebar on mobile after navigation
                        const isMobile = window.innerWidth < 1024;
                        if(isMobile) {
                            setIsSidebarOpen(false);
                            setOpenSections({});
                            setOpenSection(null);
                        }
                    }}
                >
                    {isSidebarOpen && (
                        <img
                            src={`/${
                                process.env.REACT_APP_LOGO_WHITE ||
                                "logo-with-white-text.png"
                            }`}
                            className="w-12 sm:w-16 lg:w-20 h-auto object-contain"
                            alt="Logo"
                        />
                    )}
                </div>

                {/* Toggle Button (Right) */}
                <button
                    onClick={toggleSidebar}
                    className="p-1 sm:p-1.5 lg:p-2 hover:bg-white hover:bg-opacity-20 rounded-md transition-colors"
                >
                    <FaBars
                        size={
                            isSidebarOpen
                                ? window.innerWidth < 640
                                    ? 16
                                    : 20
                                : window.innerWidth < 640
                                  ? 14
                                  : 16
                        }
                    />
                </button>
            </div>

            {/* Rest of the Sidebar */}

            <nav className="flex-1 overflow-y-auto px-1 sm:px-2 lg:px-0">
                {isSidebarOpen ? (
                    <>
                        {/* Show general menu for all users, including admins */}
                        {menu.general?.map((section) => (
                            <div
                                key={section.id}
                                className="mb-1 sm:mb-2 w-full"
                            >
                                <div
                                    className="flex justify-between items-center text-[11px] sm:text-[13px] font-medium uppercase text-white hover:text-navy cursor-pointer p-1.5 sm:p-2 hover:bg-[#C2D7E2] rounded-md mx-1 sm:mx-2"
                                    onClick={() =>
                                        handleSectionClick(
                                            section.id,
                                            section.title,
                                            section.children &&
                                                section.children.length > 0,
                                        )
                                    }
                                >
                                    <span className="flex items-center space-x-1 sm:space-x-2">
                                        {(() => {
                                            const Icon = getFaIcon(
                                                section.icon,
                                            );
                                            return Icon ? (
                                                <Icon
                                                    size={
                                                        window.innerWidth < 640
                                                            ? 14
                                                            : isSidebarOpen
                                                              ? 18
                                                              : 16
                                                    }
                                                    color="#D4AF37"
                                                />
                                            ) : (
                                                <FaBars
                                                    size={
                                                        window.innerWidth < 640
                                                            ? 14
                                                            : isSidebarOpen
                                                              ? 18
                                                              : 16
                                                    }
                                                    color="#D4AF37"
                                                />
                                            );
                                        })()}
                                        <span className="truncate">
                                            {section.title}
                                        </span>
                                    </span>
                                    {section.children &&
                                        section.children.length > 0 && (
                                            <FaChevronDown
                                                size={
                                                    window.innerWidth < 640
                                                        ? 10
                                                        : 14
                                                }
                                                className="flex-shrink-0"
                                            />
                                        )}
                                </div>
                                {openSection === section.id && (
                                    <div className="px-1 sm:px-2">
                                        {section.children.map((item) => (
                                            <MenuItem
                                                key={item.id}
                                                item={item}
                                                openSections={openSections}
                                                currentPage={currentPage}
                                                toggleSection={toggleSection}
                                                isParent={true}
                                                isSidebarOpen={isSidebarOpen}
                                                setIsSidebarOpen={
                                                    setIsSidebarOpen
                                                }
                                                setOpenSections={
                                                    setOpenSections
                                                }
                                                setOpenSection={setOpenSection}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* If admin or has core_admin items assigned, show them below general menu */}
                        {menu?.core_admin?.length > 0 && (
                            <>
                                {userRole === "admin" && (
                                <div className="text-xs uppercase text-[#C2D7E2] pl-2 sm:pl-4 pt-2 sm:pt-3 pb-2 sm:pb-3 font-semibold">
                                    Administration
                                </div>
                                )}
                                {/* Render the rest of the admin menu */}
                                {menu?.core_admin.length > 0 &&
                                    menu.core_admin.map((section) => (
                                        <div
                                            key={section.id}
                                            className="mb-1 sm:mb-2 w-full"
                                        >
                                            <div
                                                className="flex justify-between items-center text-[11px] sm:text-[13px] font-medium uppercase text-white hover:text-navy cursor-pointer p-1.5 sm:p-2 hover:bg-[#C2D7E2] rounded-md mx-1 sm:mx-2"
                                                onClick={() =>
                                                    handleSectionClick(
                                                        section.id,
                                                        section.title,
                                                        section.children &&
                                                            section.children
                                                                .length > 0,
                                                    )
                                                }
                                            >
                                                <span className="flex items-center space-x-1 sm:space-x-2">
                                                    {(() => {
                                                        const Icon = getFaIcon(
                                                            section.icon,
                                                        );
                                                        return Icon ? (
                                                            <Icon
                                                                size={
                                                                    window.innerWidth <
                                                                    640
                                                                        ? 14
                                                                        : isSidebarOpen
                                                                          ? 18
                                                                          : 16
                                                                }
                                                                color="#D4AF37"
                                                            />
                                                        ) : (
                                                            <FaBars
                                                                size={
                                                                    window.innerWidth <
                                                                    640
                                                                        ? 14
                                                                        : isSidebarOpen
                                                                          ? 18
                                                                          : 16
                                                                }
                                                                color="#D4AF37"
                                                            />
                                                        );
                                                    })()}

                                                    <span className="text-xs sm:text-sm truncate">
                                                        {section.title}
                                                    </span>
                                                </span>
                                                {section.children &&
                                                    section.children.length >
                                                        0 && (
                                                        <FaChevronDown
                                                            size={
                                                                window.innerWidth <
                                                                640
                                                                    ? 10
                                                                    : 14
                                                            }
                                                            className="flex-shrink-0"
                                                        />
                                                    )}
                                            </div>
                                            {openSection === section.id && (
                                                <div className="px-1 sm:px-2">
                                                    {section.children.map(
                                                        (item) => (
                                                            <MenuItem
                                                                key={item.id}
                                                                item={item}
                                                                openSections={
                                                                    openSections
                                                                }
                                                                currentPage={
                                                                    currentPage
                                                                }
                                                                toggleSection={
                                                                    toggleSection
                                                                }
                                                                isParent={true}
                                                                isSidebarOpen={
                                                                    isSidebarOpen
                                                                }
                                                                setIsSidebarOpen={
                                                                    setIsSidebarOpen
                                                                }
                                                                setOpenSections={
                                                                    setOpenSections
                                                                }
                                                                setOpenSection={
                                                                    setOpenSection
                                                                }
                                                            />
                                                        ),
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                            </>
                        )}
                    </>
                ) : (
                    // COLLAPSED SIDEBAR VIEW
                    <>
                        {getAllMenuItems().length === 0 ? (
                            <div className="text-white text-xs text-center mt-4">
                                Loading...
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-start pt-2 sm:pt-4 lg:pt-8 space-y-1 sm:space-y-2 lg:space-y-3">
                                {getAllMenuItems()
                                    .filter(
                                        (item) =>
                                            !item.children ||
                                            item.children.length === 0,
                                    )
                                    .map((item) => {
                                        const iconComponent = getFaIcon(
                                            item.icon,
                                        );

                                        const urlPath =
                                            item.seo_page &&
                                            item.seo_page.trim() !== ""
                                                ? resolveMenuPath(item.seo_page)
                                                : item.title;

                                        return (
                                            <div
                                                key={item.id}
                                                className={`flex justify-center items-center cursor-pointer p-1.5 sm:p-2 hover:bg-[#C2D7E2] rounded-md w-8 h-8 sm:w-10 sm:h-10 ${
                                                    currentPage === urlPath
                                                        ? "bg-[#C2D7E2]"
                                                        : ""
                                                }`}
                                                onClick={() => {
                                                    navigate(`/${urlPath}`);
                                                    const isMobile =
                                                        window.innerWidth <
                                                        1024;
                                                    if(isMobile) {
                                                        setIsSidebarOpen(false);
                                                        setOpenSections({});
                                                        setOpenSection(null);
                                                    }
                                                }}
                                                title={item.title}
                                            >
                                                {iconComponent ? (
                                                    React.createElement(
                                                        iconComponent,
                                                        {
                                                            size:
                                                                window.innerWidth <
                                                                640
                                                                    ? 12
                                                                    : 14,
                                                            color: "#D4AF37",
                                                        },
                                                    )
                                                ) : (
                                                    <span className="text-xs text-white font-bold">
                                                        {item.title?.charAt(
                                                            0,
                                                        ) ?? "?"}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                    </>
                )}
            </nav>

            {/* Social Footer */}
            {isSidebarOpen && (
                <div
                    className="flex-shrink-0 w-full px-2 sm:px-3 lg:px-4 pt-3 sm:pt-4 pb-3 sm:pb-4 lg:pb-6 border-t border-gray-700 bg-[#C2D7E2] text-center"
                    style={{ backgroundColor: "var(--navy)" }}
                >
                    {/* Brand */}
                    <p
                        className="text-xs sm:text-sm lg:text-base text-[#D4AF37] font-semibold tracking-wide mb-1 sm:mb-2 lg:mb-3 cursor-pointer"
                        onClick={() =>
                            window.open(
                                process.env.REACT_APP_WEBSITE_URL,
                                "_blank",
                            )
                        }
                    >
                        AQi
                    </p>

                    {/* Social Icons */}
                    <div className="flex justify-center gap-2 sm:gap-3 lg:gap-5">
                        <a
                            href="https://www.linkedin.com/company/aequitasinvest/posts/?feedView=all"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#D4AF37] hover:text-[#C2D7E2] transition-transform transform hover:scale-110"
                            title="LinkedIn"
                        >
                            <i className="fab fa-linkedin-in text-sm sm:text-base lg:text-lg"></i>
                        </a>
                        <a
                            href="https://www.instagram.com/aequitas.invest/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#D4AF37] hover:text-[#C2D7E2] transition-transform transform hover:scale-110"
                            title="Instagram"
                        >
                            <i className="fab fa-instagram text-sm sm:text-base lg:text-lg"></i>
                        </a>
                        <a
                            href="https://www.facebook.com/AQinv"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#D4AF37] hover:text-[#C2D7E2] transition-transform transform hover:scale-110"
                            title="Facebook"
                        >
                            <i className="fab fa-facebook-f text-sm sm:text-base lg:text-lg"></i>
                        </a>
                        <a
                            href="mailto:contact@aequitasinvest.com"
                            className="text-[#D4AF37] hover:text-[#C2D7E2] transition-transform transform hover:scale-110"
                            title="Email"
                        >
                            <i className="fas fa-envelope text-sm sm:text-base lg:text-lg"></i>
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

Sidebar.propTypes = {
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        role: PropTypes.string,
    }),
    isSidebarOpen: PropTypes.bool.isRequired,
    setIsSidebarOpen: PropTypes.func.isRequired,
};

export default Sidebar;
